/**
 * Opening a lesson: one place, so every Join button in the portal behaves the
 * same.
 *
 * The call lives at its own address (/school/call/:bookingId) and opens in its
 * own tab, mirroring the student side (web-frontend/utils/sessionRoom.js). That
 * is the point of the page rather than a modal: teaching should survive the
 * instructor navigating to a student's record mid-lesson, reloading after a
 * crash, or putting the call on a second screen — none of which a modal over
 * the Teaching page survived, and one stray click on its backdrop ended it.
 *
 * `noopener` is not optional. Without it the new tab holds a live
 * `window.opener` handle back into the portal it came from, and that tab is a
 * logged-in school-admin session.
 *
 * It is opened by clicking a synthetic anchor rather than by window.open, for
 * a reason worth reading before changing it back — see openSessionRoom.
 */
export function sessionRoomPath(bookingId, { group = false } = {}) {
  return `/school/call/${encodeURIComponent(bookingId)}${group ? "?group=1" : ""}`;
}

export function openSessionRoom(bookingId, { group = false } = {}) {
  if (!bookingId) return;
  if (typeof document === "undefined") return;
  const path = sessionRoomPath(bookingId, { group });

  /**
   * An anchor click, NOT window.open.
   *
   * `window.open(url, "_blank", "noopener")` returns null BY SPEC when noopener
   * is set: the whole point is that the caller gets no handle on the new
   * window. The previous version read that null as "the popup was blocked" and
   * fell back to navigating the current tab, so every Join opened the lesson in
   * a new tab AND threw away the page the student was already on. The fallback
   * fired on success, every time.
   *
   * A synthetic anchor gets the same rel="noopener noreferrer" protection with
   * no return value to misread. Blocked-popup handling goes with it, and
   * should: a blocker suppressing a user-gesture click is rare, and silently
   * navigating away from what someone was doing is worse than one click that
   * appears to miss.
   */
  const a = document.createElement("a");
  a.href = path;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
}


/**
 * "Am I already in this lesson?", across tabs.
 *
 * The call opens in its own tab, so the portal tab holding the join prompt has
 * no direct way to know the instructor is already in the room — and a prompt
 * inviting you to join a call you are sitting in is noise at best. Mirrors
 * web-frontend/utils/sessionRoom.js so both sides behave identically.
 *
 * A HEARTBEAT, NOT A FLAG. A crashed or force-closed tab never runs its
 * cleanup, and a stale "in session" flag would silently suppress the prompt for
 * every future lesson. The room tab rewrites the timestamp while it is open;
 * anything older than STALE_MS is treated as gone.
 */
const PRESENCE_KEY = "xair_active_session";
export const PRESENCE_HEARTBEAT_MS = 20000;
const STALE_MS = 60000;

export function markInSession(bookingId) {
  try {
    window.localStorage.setItem(PRESENCE_KEY, JSON.stringify({ bookingId: String(bookingId), at: Date.now() }));
  } catch (e) { /* private mode, quota: presence is an optimisation, never a requirement */ }
}

export function clearInSession() {
  try { window.localStorage.removeItem(PRESENCE_KEY); } catch (e) { /* as above */ }
}

/** True only for a live, non-stale presence on this exact booking. */
export function isInSession(bookingId) {
  try {
    const raw = window.localStorage.getItem(PRESENCE_KEY);
    if (!raw) return false;
    const p = JSON.parse(raw);
    if (String(p.bookingId) !== String(bookingId)) return false;
    return Date.now() - (p.at || 0) < STALE_MS;
  } catch (e) {
    return false;
  }
}
